'use client'

import { getPlatformName } from '@/actions/platform'
import ImageWithFallback from '@/components/common/image-with-fallback'
import ControllerIcon from '@/components/icons/controller-icon'
import DownloadIcon from '@/components/icons/download'
import EyeCon from '@/components/icons/eye'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import TrackedLink from '@/components/utils/tracking/tracked-link'
import { getCurrencySymbol } from '@/helpers/currency'
import { formatLiveryUpdatedDate } from '@/helpers/datetime'
import getUserName from '@/helpers/get-user-name'
import { urlSafeProfilePath } from '@/helpers/url-safe-profile-path'
import { getUserProfilePictureUrl } from '@/helpers/user'
import Image from 'next/image'
import Link from 'next/link'
import React, { useMemo } from 'react'
import LiveryBuyButton from '../../marketplace/purchase/buy-button'

function LiveryCard({ livery, session, hideStats, hidePrice }) {
  const _hidePrice = hidePrice || livery.game?.liveryMarketplace === 'limited'
  const updatedDate = useMemo(
    () => formatLiveryUpdatedDate(livery?.updateTs),
    [livery?.updateTs],
  )

  const _hideStats =
    hideStats ||
    (livery?._stats?.downloads?.total || 0) +
      (livery?._stats?.views?.total || 0) <
      20

  return (
    <div className="group flex h-full w-full flex-col overflow-hidden rounded-sm border border-neutral-blue-700 bg-neutral-blue font-inter">
      {/* Header Image */}
      <TrackedLink
        href={`/sim-racing-liveries/${livery.id}`}
        data-tracking-event="livery card click"
        trackingdata={{ livery }}
        className="aspect-341/192 relative"
      >
        <ImageWithFallback
          fill
          className="rounded-sm object-cover transition-all duration-500 group-hover:scale-105 group-hover:opacity-70"
          src={
            livery?.files?.find(
              (file) => file.usageHint === 'user-livery-image',
            )?.fileUrl
          }
          fallbackUrl="/img/default/header.webp"
          alt="Livery Header Image"
        />
      </TrackedLink>

      <div className="flex h-full flex-col justify-between px-8 pb-[23px] pt-[29px]">
        <div className="flex h-full flex-col gap-3">
          {/* Stats */}
          {!_hideStats && (
            <div className="flex items-center gap-4 desktop-xs:gap-5">
              {/* Downloads */}
              <div className="flex items-center gap-1">
                <DownloadIcon className="size-4 stroke-neutral-blue-200 stroke-1 desktop-xs:size-5" />
                <p className="text-cap font-bold text-neutral-blue-200 desktop-xs:text-p-1">
                  {livery?._stats?.downloads?.total}
                </p>
              </div>
              {/* Views */}
              <div className="flex items-center gap-1">
                <EyeCon className="size-4 stroke-neutral-blue-200 desktop-xs:size-5" />
                <p className="text-cap font-bold text-neutral-blue-200 desktop-xs:text-p-1">
                  {livery?._stats?.views?.total}
                </p>
              </div>
            </div>
          )}

          {/* Info */}
          <TrackedLink
            href={`/sim-racing-liveries/${livery.id}`}
            data-tracking-event="livery card click"
            trackingdata={{ livery }}
          >
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <h5 className="truncate-3 text-h-5-sm font-semibold transition-all group-hover:text-shades-0/85 desktop-xs:text-h-5">
                    {livery?.name || 'Unnamed Livery'}
                  </h5>
                </TooltipTrigger>
                <TooltipContent align="end">
                  <p className="text-p-2">{livery?.name || 'Unnamed Livery'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TrackedLink>

          {updatedDate && (
            <p className="text-footer font-semibold text-neutral-blue-300">
              Updated {updatedDate}
            </p>
          )}

          <TrackedLink
            href={urlSafeProfilePath({
              ...livery?.user,
              profileType: 'livery',
            })}
            data-tracking-event="livery card click"
            trackingdata={{ livery }}
          >
            <div className="flex items-center gap-2">
              <div className="relative size-9">
                <Image
                  height={36}
                  width={36}
                  src={getUserProfilePictureUrl(livery?.user)}
                  alt="Profile Picture"
                  className="w-full rounded-full"
                />
              </div>
              <p className="text-p-1 font-bold text-neutral-blue-200">
                {getUserName({ user: livery?.user })}
              </p>
            </div>
          </TrackedLink>

          <div className="mb-0 mt-auto flex items-center justify-between pt-4">
            <div className="flex h-8 w-fit max-w-[80%] items-center gap-2 rounded-full bg-warning-50 px-4 desktop-xs:h-9">
              <ControllerIcon className="size-4 stroke-warning-700 stroke-2 desktop-xs:size-5" />
              <p className="truncate-1 w-fit text-cap font-bold text-warning-700 desktop-xs:text-p-1">
                {livery?.game?.nicename}
                {livery.platforms.length > 0 && (
                  <>
                    {' - '}
                    {getPlatformName(livery.platforms)}
                  </>
                )}
              </p>
            </div>

            {!_hidePrice && (
              <div className="flex items-center gap-px text-h-6 font-semibold">
                {livery.price > 0 ? (
                  <>
                    <p>{getCurrencySymbol(livery?.currencyId)}</p>
                    <p>{livery?.price}</p>
                  </>
                ) : (
                  <p className="text-h-6 font-semibold text-success-500">
                    Free
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 flex justify-between gap-3">
          <Button
            asChild
            className="h-[62px]! flex-1! border! border-neutral-blue-600! bg-neutral-blue-700! font-inter! text-p-2! font-semibold! text-shades-0! hover:bg-shades-0! hover:text-neutral-blue-900! transition-all duration-300 ease-in-out"
          >
            <Link
              href={`/sim-racing-liveries/${livery.id}`}
              className=""
              target="_blank"
              rel="noopener noreferrer"
            >
              View
            </Link>
          </Button>
          <LiveryBuyButton
            className="h-full w-min flex-1"
            livery={livery}
            session={session}
          />
        </div>
      </div>
    </div>
  )
}

export default React.memo(LiveryCard)
